import { DateTime } from 'luxon';
import React from 'react';

import Time from 'src/enosikit/components/Time';

/**
 * Formats a time range for standalone display with semantic HTML using React Time components.
 * Returns an "Active ..." style summary suitable for standalone contexts.
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization.
 * @param {object} active - The active start and finish timestamps.
 * @param {number} [active.start] - The active start timestamp in seconds since epoch.
 * @param {number} [active.finish] - The active finish timestamp in seconds since epoch.
 * @returns {React.ReactNode} - A formatted time range message with Time components.
 */
const buildSummary = (intl, active) => {
  const { start, finish } = active || {};
  const startOnly = start && !finish;
  const finishOnly = !start && finish;
  const startAndFinish = start && finish;

  if (startOnly) {
    const startDate = (
      <Time
        child={DateTime.fromSeconds(start).toISO()}
        unixTimestamp={start}
      />
    );
    return intl.formatMessage({ id: 'util.i18n.time_range.build_summary.start_only.text', defaultMessage: 'Active from {startDate}' }, { startDate });
  }
  if (finishOnly) {
    const finishDate = (
      <Time
        child={DateTime.fromSeconds(finish).toISO()}
        unixTimestamp={finish}
      />
    );
    return intl.formatMessage({ id: 'util.i18n.time_range.build_summary.finish_only.text', defaultMessage: 'Active until {finishDate}' }, { finishDate });
  }
  if (startAndFinish) {
    const startDate = (
      <Time
        child={DateTime.fromSeconds(start).toISO()}
        unixTimestamp={start}
      />
    );
    const finishDate = (
      <Time
        child={DateTime.fromSeconds(finish).toISO()}
        unixTimestamp={finish}
      />
    );

    return intl.formatMessage({ id: 'util.i18n.time_range.build_summary.start_and_finish.text', defaultMessage: 'Active from {startDate} until {finishDate}' }, { startDate, finishDate });
  }
  return intl.formatMessage({ id: 'util.i18n.time_range.build_summary.active_always.text', defaultMessage: 'Active always' });
};

export default buildSummary;
